import React, { useState, useEffect } from 'react';

/**
 * PostCallPanel — appears after a call ends, shows result + AI summary.
 * Fixed bottom-right, auto-dismisses after 10s (parent handles timer).
 */
export default function PostCallPanel({ callData, onDismiss, onOpenCallLog }) {
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [checkedItems, setCheckedItems] = useState([]);

  const { from_number, from_name, direction, result, duration } = callData || {};

  // Poll for AI summary after 15s delay
  useEffect(() => {
    if (!from_number) {
      setSummaryLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({ limit: '1', from_number });
        const res = await fetch(`/api/voip/calls?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const call = Array.isArray(data) ? data[0] : data?.calls?.[0];
          if (call && call.ai_summary) {
            setSummaryData({
              summary: call.ai_summary,
              follow_up_required: call.follow_up_required || false,
              action_items: call.action_items || [],
              urgency: call.urgency,
            });
            setCheckedItems(new Array((call.action_items || []).length).fill(false));
          }
        }
      } catch (e) {
        console.warn('PostCallPanel: failed to fetch summary', e);
      } finally {
        setSummaryLoading(false);
      }
    }, 15000);

    return () => clearTimeout(timer);
  }, [from_number]);

  // Format duration as m:ss
  const fmtDuration = (secs) => {
    if (!secs && secs !== 0) return '--:--';
    const s = Number(secs);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  // Result badge config
  const badgeConfig = (r) => {
    const v = (r || '').toLowerCase();
    if (v === 'completed' || v === 'answered' || v === 'accepted')
      return { label: r || 'Completed', cls: 'bg-green-500/20 text-green-400 border-green-500/30' };
    if (v === 'missed' || v === 'no answer' || v === 'noanswer')
      return { label: 'Missed', cls: 'bg-red-500/20 text-red-400 border-red-500/30' };
    if (v === 'voicemail' || v === 'voicemail deposit')
      return { label: 'Voicemail', cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
    if (v === 'transferred' || v === 'transfer')
      return { label: 'Transferred', cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
    if (v === 'failed' || v === 'busy' || v === 'rejected')
      return { label: r || 'Failed', cls: 'bg-red-500/20 text-red-400 border-red-500/30' };
    return { label: r || 'Ended', cls: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
  };

  const badge = badgeConfig(result);

  const toggleCheck = (i) => {
    setCheckedItems((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9998] w-[400px] rounded-xl bg-gray-900 border border-gray-700 shadow-2xl shadow-black/50 overflow-hidden">
      {/* Countdown drain bar */}
      <div
        className="h-0.5 bg-gray-500"
        style={{
          animation: 'postcall-drain 10s linear forwards',
          transformOrigin: 'left',
        }}
      />
      <style>{`@keyframes postcall-drain { from { width: 100%; } to { width: 0%; } }`}</style>

      {/* HEADER */}
      <div className="px-4 pt-3 pb-3 border-b border-gray-700/50">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Call Ended</span>
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
          <button
            onClick={onDismiss}
            className="text-gray-500 hover:text-gray-300 transition-colors text-lg leading-none"
          >
            &times;
          </button>
        </div>
        <div className="text-lg font-bold text-white">
          {from_name || from_number || 'Unknown'}
        </div>
        <div className="flex items-center gap-3 mt-1 text-sm text-gray-400">
          <span>{direction === 'Inbound' || direction === 'inbound' ? '\u2199 Inbound' : '\u2197 Outbound'}</span>
          <span className="text-gray-600">{'\u00b7'}</span>
          <span>{fmtDuration(duration)}</span>
        </div>
      </div>

      {/* AI SUMMARY */}
      <div className="px-4 py-3 border-b border-gray-700/50">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">AI Summary</span>
          {summaryLoading && (
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
            </span>
          )}
        </div>
        {summaryLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>Processing...</span>
          </div>
        ) : summaryData?.summary ? (
          <div>
            <p className="text-sm text-gray-300 leading-relaxed">{summaryData.summary}</p>
            {summaryData.follow_up_required && (
              <span className="inline-block mt-2 text-[11px] font-semibold px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/30">
                Follow-up required
              </span>
            )}
            {summaryData.urgency && summaryData.urgency !== 'normal' && (
              <span className={`inline-block mt-2 ml-1.5 text-[11px] font-semibold px-2 py-0.5 rounded border ${
                summaryData.urgency === 'high' || summaryData.urgency === 'urgent'
                  ? 'bg-red-500/20 text-red-400 border-red-500/30'
                  : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
              }`}>
                {summaryData.urgency.charAt(0).toUpperCase() + summaryData.urgency.slice(1)} urgency
              </span>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-600">No summary available</p>
        )}
      </div>

      {/* ACTION ITEMS */}
      {summaryData?.action_items?.length > 0 && (
        <div className="px-4 py-3 border-b border-gray-700/50">
          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Action Items</div>
          {summaryData.action_items.map((item, i) => (
            <label key={i} className="flex items-start gap-2 py-1 cursor-pointer group">
              <input
                type="checkbox"
                checked={checkedItems[i] || false}
                onChange={() => toggleCheck(i)}
                className="mt-0.5 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500/30 focus:ring-offset-0"
              />
              <span className={`text-sm leading-snug transition-colors ${
                checkedItems[i] ? 'text-gray-600 line-through' : 'text-gray-300'
              }`}>
                {typeof item === 'string' ? item : item.description || item.text || JSON.stringify(item)}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* QUICK ACTIONS */}
      <div className="px-4 py-3">
        <div className="flex gap-2">
          <button
            onClick={() => { onOpenCallLog(); onDismiss(); }}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-3 rounded-lg transition-colors text-sm"
          >
            View Call Log
          </button>
          <button
            onClick={() => console.log('TODO: Create task from call', callData)}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-3 rounded-lg transition-colors text-sm"
          >
            Create Task
          </button>
          <button
            onClick={onDismiss}
            className="px-3 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
